import { type ColumnDef, flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import type { ReactNode } from "react";

import { Button } from "./Button";
import { cn } from "./lib/cn";
import { Spinner } from "./Spinner";

export interface TablePagination {
  /** S3 listQuery.page ile aynı (1 tabanlı). */
  page: number;
  /** S3 listQuery.limit ile aynı. */
  limit: number;
  /** S1 ListMeta.total ile aynı. */
  total: number;
  onPageChange: (page: number) => void;
}

export interface TableProps<T> {
  columns: ColumnDef<T>[];
  data: T[];
  /** Sunucu taraflı sayfalama — meta'yı S1 zarfından geçirin. */
  pagination?: TablePagination;
  loading?: boolean;
  /** Boş veri mesajı; verilmezse Türkçe varsayılan. */
  empty?: ReactNode;
  className?: string;
}

export function Table<T>({
  columns,
  data,
  pagination,
  loading = false,
  empty,
  className,
}: TableProps<T>) {
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
  });
  const totalPages = pagination ? Math.max(1, Math.ceil(pagination.total / pagination.limit)) : 1;

  return (
    <div
      className={cn(
        "overflow-hidden rounded-lg border border-border-default bg-surface",
        className,
      )}
    >
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          {/**
           * UI Tur 2 §5: başlık satırı 44px, SURFACE zemin (muted değil) + alt 1px
           * border-default; tablo kartın içinde tek parça beyaz duruyor.
           */}
          <thead className="border-b border-border-default">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="h-11">
                {headerGroup.headers.map((header) => (
                  <th key={header.id} className="px-4 text-[13px] font-medium text-fg-muted">
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-12 text-center">
                  <Spinner className="mx-auto" />
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-8 text-center text-fg-soft">
                  {empty ?? "Kayıt bulunamadı"}
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  className="border-b border-border-subtle transition-colors last:border-b-0 hover:bg-muted"
                >
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="px-4 py-4 text-fg">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {pagination ? (
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border-subtle px-5 py-3">
          <p className="text-xs text-fg-soft">
            Toplam {pagination.total} kayıt • Sayfa {pagination.page} / {totalPages}
          </p>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              disabled={pagination.page <= 1}
              onClick={() => pagination.onPageChange(pagination.page - 1)}
            >
              Önceki
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={pagination.page >= totalPages}
              onClick={() => pagination.onPageChange(pagination.page + 1)}
            >
              Sonraki
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
